import { useGetPublishedPostsQuery, useGetPublicCategoriesQuery } from "../../../../../app/websiteApiSlice";
import { useGetPublicSchoolInfoQuery } from "../../../../../app/authApiSlice";
import WebsiteHeader from "../../WebsiteHeader";
import WebsiteFooter from "../../WebsiteFooter";
import SeoHead from "../../SeoHead";
import "./bold.css";
import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia,
  CardActionArea, Chip, CircularProgress, Button, useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";

export default function BoldTheme({ settings }) {
  const { data: school } = useGetPublicSchoolInfoQuery();
  const { data: postsData, isLoading } = useGetPublishedPostsQuery({ per_page: 7 });
  const { data: categories = [] } = useGetPublicCategoriesQuery();
  const muiTheme = useTheme();
  const accent = muiTheme.palette.primary.main;

  const posts = postsData?.posts || [];
  const heroPost = posts[0];
  const gridPosts = posts.slice(1);

  return (
    <Box className="website-bold" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#0f0f0f" }}>
      <SeoHead title={settings?.tagline || school?.name} description={settings?.tagline} schoolName={school?.name} />
      <WebsiteHeader school={school} navItems={settings?.nav_items} variant="bold" />

      {/* Hero post */}
      {heroPost ? (
        <Box
          component={Link}
          to={`/blog/${heroPost.slug}`}
          sx={{ position: "relative", display: "block", textDecoration: "none", color: "white", "&:hover .hero-overlay": { opacity: 0.75 } }}
        >
          <Box
            component="img"
            src={heroPost.featured_image || ""}
            alt={heroPost.title}
            sx={{ width: "100%", height: { xs: 300, md: 520 }, objectFit: "cover", display: "block" }}
          />
          <Box
            className="hero-overlay"
            sx={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%)",
              transition: "opacity 0.2s",
            }}
          />
          <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: { xs: 3, md: 5 } }}>
            <Box display="flex" gap={0.5} mb={1.5}>
              {heroPost.categories?.map((c) => (
                <Chip key={c.id} label={c.name} size="small" sx={{ bgcolor: accent, color: "white", fontWeight: 700 }} />
              ))}
            </Box>
            <Typography variant="h3" fontWeight={900} sx={{ color: "white", fontSize: { xs: "1.6rem", md: "2.8rem" } }}>
              {heroPost.title}
            </Typography>
            {heroPost.published_at && (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5, display: "block" }}>
                {format(new Date(heroPost.published_at), "MMMM d, yyyy")}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        /* Fallback hero if no posts */
        <Box sx={{ bgcolor: accent, py: { xs: 8, md: 14 }, textAlign: "center", color: "white" }}>
          <Container maxWidth="md">
            <Typography variant="h2" fontWeight={900}>{school?.name}</Typography>
            {settings?.tagline && <Typography variant="h6" sx={{ opacity: 0.9, mt: 1 }}>{settings.tagline}</Typography>}
          </Container>
        </Box>
      )}

      {/* Category chips */}
      {categories.length > 0 && (
        <Box sx={{ bgcolor: "#1a1a1a", py: 1.5, overflowX: "auto" }}>
          <Container maxWidth="lg">
            <Box display="flex" gap={1}>
              {categories.map((c) => (
                <Chip
                  key={c.id}
                  label={c.name}
                  component={Link}
                  to={`/blog/category/${c.slug}`}
                  clickable
                  variant="outlined"
                  sx={{ color: "white", borderColor: "white", "&:hover": { bgcolor: accent, borderColor: accent } }}
                />
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Grid posts */}
      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={900} sx={{ color: "white" }}>More Stories</Typography>
          <Button component={Link} to="/blog" sx={{ color: accent }}>All Posts →</Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: accent }} /></Box>
        ) : (
          <Grid container spacing={2}>
            {gridPosts.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <Card sx={{ borderRadius: 2, bgcolor: "#1a1a1a", height: "100%", "&:hover": { transform: "translateY(-4px)", transition: "0.2s" } }}>
                  <CardActionArea component={Link} to={`/blog/${post.slug}`}>
                    {post.featured_image && (
                      <CardMedia component="img" height="180" image={post.featured_image} alt={post.title} />
                    )}
                    <CardContent>
                      <Box display="flex" gap={0.5} mb={1}>
                        {post.categories?.slice(0, 2).map((c) => (
                          <Chip key={c.id} label={c.name} size="small" sx={{ bgcolor: accent, color: "white", fontSize: "0.65rem" }} />
                        ))}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ color: "white" }}>{post.title}</Typography>
                      {post.published_at && (
                        <Typography variant="caption" sx={{ color: "grey.500" }}>
                          {format(new Date(post.published_at), "MMM d, yyyy")}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <WebsiteFooter settings={settings} school={school} variant="bold" />
    </Box>
  );
}
